import type { ProjectSummary } from '@shared/api';
import type { PresetDto } from '@shared/views';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LayoutTemplate, Play, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router';
import { Button } from '../../components/ui/button';
import { Card, CardBody, CardHeader } from '../../components/ui/card';
import { Alert, Badge } from '../../components/ui/feedback';
import { api } from '../../lib/api';
import { queryKeys } from '../../lib/query';
import { errorMessage } from '../../lib/session';

interface UsePresetResponse {
  project: ProjectSummary;
  jobId: string | null;
}

/**
 * Saved research templates.
 *
 * Hidden entirely until the workspace has one, so a first-time user never sees an empty box
 * asking about a concept they have not met yet. Saving a template happens on a finished run,
 * where there is something worth saving.
 */
export function Templates() {
  const client = useQueryClient();
  const navigate = useNavigate();

  const presets = useQuery({ queryKey: queryKeys.presets, queryFn: () => api.get<PresetDto[]>('/api/presets') });

  const run = useMutation({
    mutationFn: (id: string) => api.post<UsePresetResponse>(`/api/presets/${encodeURIComponent(id)}/use`, { start: true }),
    onSuccess: async (result) => {
      await client.invalidateQueries({ queryKey: queryKeys.projects });
      void navigate(`/research/${result.project.id}`);
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete<{ ok: true }>(`/api/presets/${encodeURIComponent(id)}`),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: queryKeys.presets });
    },
  });

  const list = presets.data ?? [];
  if (list.length === 0) return null;

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            <LayoutTemplate className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            Templates
          </span>
        }
        description="Research you saved to run again."
      />
      <CardBody className="space-y-2">
        {run.error ? <Alert tone="error">{errorMessage(run.error)}</Alert> : null}

        {list.map((preset) => (
          <div key={preset.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-2 last:border-b-0 last:pb-0">
            <div className="min-w-0">
              <p className="truncate text-[13px] font-medium text-foreground">{preset.name}</p>
              <p className="mt-0.5 truncate text-[13px] text-muted-foreground">
                {preset.description ?? preset.keywords.join(', ')}
                {preset.useCount > 0 ? ` · used ${String(preset.useCount)}×` : ''}
              </p>
            </div>
            <span className="flex shrink-0 items-center gap-1.5">
              {preset.sources.map((source) => (
                <Badge key={source} tone="neutral">
                  {source.replace(/_/g, ' ')}
                </Badge>
              ))}
              <Button
                size="sm"
                variant="outline"
                icon={<Play className="h-3.5 w-3.5" />}
                loading={run.isPending && run.variables === preset.id}
                onClick={() => run.mutate(preset.id)}
              >
                Run again
              </Button>
              <button
                type="button"
                className="text-muted-foreground transition-colors duration-150 hover:text-destructive"
                aria-label={`Delete the template "${preset.name}"`}
                onClick={() => remove.mutate(preset.id)}
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </span>
          </div>
        ))}
      </CardBody>
    </Card>
  );
}
